import type { RouteLocationNormalized } from '#vue-router';

type D2CFlowStatus = 'started' | 'completed' | '';

interface TokenObject {
  access_token: string;
  expires: string;
  refresh_token: string;
}
interface AuthI {
  setToken(data: any): Promise<void>;
  logout(): Promise<void>;
  resetToken(): void;
  getUser(forceReload?: boolean): Promise<AuthI['user']>;
  isLoggedIn: boolean;
  checkStatus(to?: RouteLocationNormalized): Promise<boolean>;
  getToken(): string | null;
  user: {
    roles: string[];
    [key: string]: any;
  };
  checkD2CFlowStatus(): D2CFlowStatus;
  setD2CFlowStatus(value: D2CFlowStatus): void;
}

export default defineNuxtPlugin({
  name: 'auth-plugin',
  async setup(app) {
    const { $axios, $config, $api, $backendLink } = useNuxtApp();

    const userToken = useCookie<TokenObject | null>('user-token', {
      sameSite: 'lax',
      secure: true,
    });

    const d2cFlow = useCookie<D2CFlowStatus>('d2c-flow', {
      sameSite: 'lax',
      secure: true,
    });

    if (userToken && userToken.value && userToken.value.access_token) {
      $axios.defaults.headers.common['Authorization'] =
        'Bearer ' + userToken.value.access_token;
    }
    const authReactive: AuthI = reactive({
      user: { roles: ['client'] },
      isLoggedIn: false,
      async checkStatus(to) {
        if (to) {
          let authCode = to.query.code ? to.query.code : '';
          const encryptedToken = to.query.memberId ? to.query.memberId : '';
          if (encryptedToken) {
            const encodedMemberId = encryptedToken;
            const body = {
              encryptedToken: encodedMemberId,
            };
            try {
              const userTokenRequest = await $axios.post(
                `${$backendLink}/oauth/decrypt`,
                body,
              );
              authCode = userTokenRequest?.data?.code;
            } catch (error) {}
          }
          // Get AuthCode from query and set tokens
          if (authCode) {
            const body = {
              accessToken: authCode,
            };
            // TODO move into endpoints
            try {
              let userTokenRequest = await $axios.post(
                `${$backendLink}/userToken`,
                body,
              );
              if (userTokenRequest.data && userTokenRequest.data.access_token) {
                authReactive.setToken(userTokenRequest.data);
              }
            } catch (error) {}
            return true;
          }
        }
        if (!userToken.value) {
          // is Guest
          return false;
        }
        if (
          userToken.value?.expires &&
          new Date() > new Date(userToken.value?.expires)
        ) {
          // Token Expired need to check for refresh
          if (!userToken.value?.refresh_token) {
            return false;
          }
          try {
            let userTokenRequest = await $axios.post(
              `${$backendLink}/security/token/refresh`,
              {
                refreshToken: userToken.value?.refresh_token,
              },
            );
            authReactive.setToken(userTokenRequest.data);
            return false;
          } catch (error) {
            authReactive.logout();
            return false;
            // LOGOUT user
          }
        }
        await authReactive.getUser();
        return false;
        // delete route.query.code;
        // return navigateTo({ path: '/'});
      },
      getToken() {
        return userToken.value && userToken.value.access_token;
      },
      async setToken(tokenObject: TokenObject) {
        userToken.value = tokenObject;
        $axios.defaults.headers.common['Authorization'] =
          'Bearer ' + tokenObject.access_token;
        await authReactive.getUser();
      },
      resetToken() {
        userToken.value = null;
        delete $axios.defaults.headers.common['Authorization'];
        authReactive.isLoggedIn = false;
        authReactive.user = { roles: ['client'] };
      },
      async getUser(forceReload: boolean = false) {
        // Check token Is Token valied
        authReactive.isLoggedIn = true;
        if (authReactive.user.roles[0] == 'user' && !forceReload) {
          return authReactive.user;
        }
        try {
          // Move to endpoins
          let headers: { [key: string]: any } = {};
          const tenantState = useTenantName();
          if (tenantState.value) {
            headers['x-tenant-id'] = tenantState.value;
            headers['X-Tenant-Id'] = tenantState.value;
          }
          const response = await $axios.get(`${$backendLink}/user/profile`, {
            headers,
          });
          const { data: userInfo } = response.data ? response : { data: {} };
          const user = {
            roles: ['user'],
            ...userInfo,
          };
          authReactive.isLoggedIn = true;
          authReactive.user = user;
          return user;
        } catch (err) {
          authReactive.logout();
          // if (err.response.status == 403) {
          //   //   authReactive.logout();
          //   //   authReactive.reset();
          // }
        }

        //internally calls check method
      },
      async logout(specialRedirection?) {
        // TODO fix location not found issue
        const { $api, ssrContext } = useNuxtApp();
        if (userToken.value) {
          if (!$api) {
            authReactive.resetToken();
            if (typeof window !== 'undefined') {
              if (specialRedirection) {
                location.href = specialRedirection.toString();
              } else {
                location.reload();
              }
            }
            if (ssrContext) {
              ssrContext.event.node.res.setHeader('location', '/');
              ssrContext.event.node.res.statusCode = 302;
              // ssrContext.event.node.res.end();
            }
            // Reload
          }
          try {
            const data = await $api.endPoints.getLogoutUrl(
              location.origin,
              'en',
            );
            //   await $auth.strategies.mcp.logout();
            authReactive.resetToken();
            if (typeof window !== 'undefined' && data?.data?.url) {
              location.href = data.data.url;
            }
          } catch (error) {
            console.error('Error logout');
          }
        } else {
          authReactive.resetToken();
          if (typeof window !== 'undefined') {
            if (specialRedirection) {
              location.href = specialRedirection.toString();
            } else {
              location.reload();
            }
          }
        }
      },
      checkD2CFlowStatus() {
        return d2cFlow.value;
      },
      setD2CFlowStatus(value: D2CFlowStatus) {
        d2cFlow.value = value;
      }
    });
    if (process.client) {
      authReactive.checkStatus();
    }

    // await authReactive.checkStatus();
    return {
      provide: {
        auth: authReactive,
      },
    };
  },
});

// export default defineNuxtPlugin(async () => {
//   const auth = new Auth();
//   return {
//     provide: {
//       auth
//     }
//   }
// })
